import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaAward, FaGlobeAmericas, FaLeaf, FaUsers } from "react-icons/fa";
import useDocumentMeta from "../hooks/useDocumentMeta";

export default function About() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    useDocumentMeta({
        title: t("seo.about.title"),
        description: t("seo.about.description"),
        canonicalPath: "/about",
        robots: "index,follow,max-image-preview:none",
    });

    const stats = [
        { value: "2024", label: t('about.stats.years') },
        { value: "50+", label: t('about.stats.clients') },
        { value: "500,000+", label: t('about.stats.tons') },
        { value: "8+", label: t('about.stats.countries') }
    ];

    const values = [
        {
            icon: FaAward,
            title: t('about.values.quality.title'),
            description: t('about.values.quality.description')
        },
        {
            icon: FaLeaf,
            title: t('about.values.sustainability.title'),
            description: t('about.values.sustainability.description')
        },
        {
            icon: FaGlobeAmericas,
            title: t('about.values.reach.title'),
            description: t('about.values.reach.description')
        },
        {
            icon: FaUsers,
            title: t('about.values.focus.title'),
            description: t('about.values.focus.description')
        }
    ];

    return (
        <main className="bg-vsblack text-white min-h-screen pt-20">
            {/* Hero Section */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0">
                    <motion.div
                        className="absolute top-1/2 left-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 bg-red-glow opacity-20 rounded-full blur-3xl"
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.2, 0.3, 0.2],
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    />
                </div>

                <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold font-serif mb-6 leading-tight">
                            <span className="text-vsred hero-text-shadow">{t('about.hero.title')}</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-4xl mx-auto break-words">
                            {t('about.hero.description')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-4xl md:text-5xl font-bold text-vsred mb-2">
                                    {stat.value}
                                </div>
                                <div className="text-gray-400">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Story Section */}
            <section className="py-20 px-6 bg-gradient-to-b from-vsblack to-vsblackLight">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2 className="text-3xl sm:text-4xl font-bold font-serif mb-6 text-vsred">
                                {t('about.story.title')}
                            </h2>
                            <div className="space-y-4 text-gray-300 leading-relaxed">
                                <p>{t('about.story.content1')}</p>
                                <p>{t('about.story.content2')}</p>
                                <p>{t('about.story.content3')}</p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="relative"
                        >
                            <div className="card-dark p-8">
                                <img
                                    src="/assets/images/prod3.jpeg"
                                    alt="VSGrill Facility"
                                    className="w-full h-64 object-cover rounded-lg mb-6"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="py-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold font-serif mb-4 text-vsred">
                            {t('about.values.title')}
                        </h2>
                        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                            {t('about.values.subtitle')}
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="card-dark p-8 text-center group"
                            >
                                <div className="mb-4 flex justify-center">
                                    <div className="w-16 h-16 bg-gradient-to-br from-vsred to-vsred-dark rounded-xl flex items-center justify-center shadow-red-glow group-hover:scale-110 transition-transform duration-300">
                                        <value.icon className="text-3xl text-white" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-white font-serif">
                                    {value.title}
                                </h3>
                                <p className="text-gray-400 text-sm leading-relaxed">
                                    {value.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6 bg-gradient-to-b from-vsblack to-vsblackLight">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="card-dark p-12"
                    >
                        <h2 className="text-2xl sm:text-3xl font-bold font-serif mb-4 text-white">
                            {t('about.cta.title')}
                        </h2>
                        <p className="text-gray-400 mb-8 text-lg">
                            {t('about.cta.description')}
                        </p>
                        <a
                            href="/#quote"
                            className="btn-primary inline-block text-lg"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/');
                                setTimeout(() => {
                                    document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' });
                                }, 100);
                            }}
                        >
                            {t('nav.requestQuote')}
                        </a>
                    </motion.div>
                </div>
            </section>
        </main>
    );
}

